import { isPremiumTierPeriodUpgrade, Tier, TierPeriod, tierGreaterThan, } from 'features/premium/entities';
import { formatAmount } from 'utils/formatting';
/**
 * Pure translation of a `StripePreview` payload (+ store-derived facts) into the
 * normalized `CalculationDescriptionModel`. Kept pure and exported so the
 * field-mapping heuristics can be unit-tested against real preview payloads.
 */
export const buildPremiumModel = (previewData, facts) => {
    const { tier, period, currency, currentTier, currentPeriod, trialDaysRemaining, startsTrial, annualDiscountPercentage, } = facts;
    const isRefund = !!previewData.refundableToday;
    let intent;
    if (currentTier === Tier.free) {
        intent = 'new';
    }
    else if (isRefund) {
        intent =
            currentTier === tier && currentPeriod === period
                ? 'samePlanRefund'
                : 'refund';
    }
    else {
        const isUpgrade = tierGreaterThan(tier, currentTier) ||
            (currentPeriod != null &&
                isPremiumTierPeriodUpgrade(tier, period, currentTier, currentPeriod));
        intent = isUpgrade ? 'upgrade' : 'downgrade';
    }
    // Credit. `previewData.credit` is the total credit applied to this invoice,
    // drawn from the persistent account-credit balance plus the one-off proration
    // credit. The account-credit portion is `credit - prorationCredit`; whatever
    // account credit it doesn't consume is the balance that carries to future
    // payments (fragment E), confirmed against staging payloads.
    const accountCreditApplied = isRefund
        ? 0
        : Math.round(previewData.credit * 100 - (previewData.prorationCredit ?? 0) * 100) / 100;
    // V6 — the discount applies to a future bill rather than today's payment.
    // Confirmed against staging: a discount applied today has `payToday` equal to
    // the discounted price, whereas a next-bill discount has `payToday === 0` with
    // the discounted charge falling on `nextChargeDate`.
    const appliesToNextBill = previewData.payToday === 0 &&
        previewData.discountPrice > 0 &&
        accountCreditApplied !== previewData.discountPrice;
    let promo;
    if (previewData.percentageDiscount?.amount) {
        promo = {
            kind: 'percent',
            label: `${previewData.percentageDiscount.percentage}%`,
            durationMonths: previewData.discountDurationMonths || undefined,
            appliesToNextBill,
        };
    }
    else if (previewData.fixedDiscount) {
        promo = {
            kind: 'amount',
            label: formatAmount(previewData.fixedDiscount, currency, 2),
            durationMonths: previewData.discountDurationMonths || undefined,
            appliesToNextBill,
        };
    }
    const creditRemaining = Math.max(Math.round((previewData.accountCredit ?? 0) * 100 - accountCreditApplied * 100) / 100, 0);
    const credit = creditRemaining > 0
        ? {
            remaining: creditRemaining,
            coversNextBill: creditRemaining > previewData.fullPrice,
        }
        : undefined;
    return {
        context: 'premium',
        tier,
        period,
        currency,
        intent,
        startsToday: intent === 'new',
        startsTrial: intent === 'new' && !!startsTrial,
        hasAnnualDiscount: period === TierPeriod.yearly,
        annualDiscountPercentage,
        trialDaysRemaining,
        promo,
        renewal: {
            price: previewData.nextChargePrice,
            date: previewData.nextChargeDate,
        },
        fullPriceRenewal: {
            price: previewData.fullPrice,
            date: previewData.fullPriceRenewalDate,
        },
        payToday: previewData.payToday,
        credit,
        refund: previewData.refundableToday
            ? { amount: previewData.refundableToday }
            : undefined,
    };
};
